import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  FormField,
  Header,
  Input,
  SpaceBetween,
  Spinner
} from '@cloudscape-design/components';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { deepEqual } from 'fast-equals';
import { RotateCcw, Save, Trash2, X } from 'lucide-react';
import { UserContext } from '../../../context/authContext';
import { DocumentContext } from '../../../context/documentContext';
import { usePolicyContext } from '../../../context/policyContext';
import { useAlert } from '../../../context/alertContext';

type PolicyValue =
  | string
  | number
  | boolean
  | null
  | PolicyValue[]
  | { [key: string]: PolicyValue };

interface PolicyAppliesToNormalized {
  text: string[];
  node_refs: string[];
}

interface PolicyNormalizationCaution {
  level: string;
  has_soft_or_hard_to_compare_wording: boolean;
  notes: string[];
}

interface PolicyNormalizedRule {
  normalized_dimension: string;
  primary_comparison_mechanic: string;
  comparison_type: string;
  expected_value_structure_type: string;
  expected_value_structure: Record<string, PolicyValue>;
  normalized_rule_statement: string;
  normalized_rule_statement_template: string;
  source_text: string;
  applies_to_normalized: PolicyAppliesToNormalized;
  normalization_caution: PolicyNormalizationCaution;
}

interface PolicyCandidateItem {
  candidate_id: string;
  normalized_rule: PolicyNormalizedRule;
  embedding: number[];
  embeddingSourceText: number[];
}

interface PolicyDetailsState {
  policyName: string;
  version: string;
  type: string;
}

interface PolicyEditorState {
  policyDetails: PolicyDetailsState;
  policyCategories: PolicyCandidateItem[];
}

interface GroupedPolicyCandidate {
  index: number;
  item: PolicyCandidateItem;
}

interface PolicyCandidateGroup {
  dimensionKey: string;
  dimensionLabel: string;
  items: GroupedPolicyCandidate[];
}

const EMPTY_POLICY_DETAILS: PolicyDetailsState = {
  policyName: '',
  version: '',
  type: ''
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const cloneValue = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const getPolicyNameFromFileName = (fileName: string | undefined) => {
  if (!fileName) {
    return '';
  }

  return fileName.replace(/\.[^/.]+$/, '');
};

const toFiniteNumber = (value: unknown): number | null => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const toStringValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (value === null || value === undefined) {
    return '';
  }

  try {
    return JSON.stringify(value);
  } catch (_error) {
    return '';
  }
};

const toPolicyValue = (value: unknown): PolicyValue => {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((entry) => toPolicyValue(entry));
  }

  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, toPolicyValue(entry)])
    );
  }

  if (value === undefined) {
    return '';
  }

  return toStringValue(value);
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => toStringValue(entry).trim())
    .filter((entry) => entry.length > 0);
};

const toBooleanValue = (value: unknown): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }

  return Boolean(value);
};

const formatFieldLabel = (value: string): string =>
  value
    .split(/[._]/)
    .filter((entry) => entry.length > 0)
    .map((entry) => entry.charAt(0).toUpperCase() + entry.slice(1))
    .join(' ');

const formatVariableLabel = (value: string): string =>
  value
    .replace(/_/g, ' ')
    .split('.')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)
    .map((entry) =>
      /^\d+$/.test(entry)
        ? `Option ${Number(entry) + 1}`
        : entry.charAt(0).toUpperCase() + entry.slice(1)
    )
    .join(' ');

const getNormalizedDimensionLabel = (value: string): string => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return 'Uncategorized';
  }

  return formatFieldLabel(trimmedValue);
};

const formatPrimitiveValue = (value: PolicyValue): string => {
  if (typeof value === 'string') {
    return value.replace(/_/g, ' ');
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (value === null) {
    return '';
  }

  return toStringValue(value);
};

const coercePrimitiveValue = (
  nextValue: string,
  previousValue: PolicyValue
): PolicyValue => {
  if (typeof previousValue === 'number') {
    const trimmedValue = nextValue.trim();

    if (!trimmedValue) {
      return '';
    }

    const parsedValue = Number(nextValue);
    return Number.isFinite(parsedValue) ? parsedValue : nextValue;
  }

  if (typeof previousValue === 'boolean') {
    const normalizedValue = nextValue.trim().toLowerCase();

    if (normalizedValue === 'true') {
      return true;
    }

    if (normalizedValue === 'false') {
      return false;
    }
  }

  return nextValue;
};

const normalizeTemplateValuePath = (expression: string): string =>
  expression.trim().replace(/\.join\(\s*(['"]).*\1\s*\)$/, '');

const getTemplateVariablePaths = (template: string): Set<string> =>
  new Set(
    Array.from(
      template.matchAll(/\$\{expected_value_structure\.([^}]+)\}/g),
      (match) => normalizeTemplateValuePath(match[1] || '')
    ).filter((path) => path.length > 0)
  );

const pathReferencesTemplate = (
  path: string,
  templateVariablePaths: Set<string>
): boolean =>
  Array.from(templateVariablePaths).some(
    (templatePath) =>
      templatePath === path ||
      templatePath.startsWith(`${path}.`) ||
      path.startsWith(`${templatePath}.`)
  );

const getNestedPolicyValue = (
  value: PolicyValue | Record<string, PolicyValue>,
  path: string
): PolicyValue | undefined =>
  path
    .split('.')
    .filter((segment) => segment.trim().length > 0)
    .reduce<PolicyValue | undefined>((currentValue, segment) => {
      if (!isRecord(currentValue) && !Array.isArray(currentValue)) {
        return undefined;
      }

      return (currentValue as Record<string, PolicyValue>)[segment];
    }, value);

const setNestedPolicyValue = (
  value: Record<string, PolicyValue>,
  path: string,
  nextValue: PolicyValue
): Record<string, PolicyValue> => {
  const segments = path
    .split('.')
    .filter((segment) => segment.trim().length > 0);

  if (segments.length === 0) {
    return value;
  }

  const updateNode = (
    currentValue: PolicyValue | Record<string, PolicyValue>,
    segmentIndex: number
  ): PolicyValue => {
    const segment = segments[segmentIndex];
    const isLastSegment = segmentIndex === segments.length - 1;
    const currentNode = currentValue ?? {};

    if (Array.isArray(currentNode)) {
      const nextArray = [...currentNode];
      const arrayIndex = Number(segment);

      if (Number.isNaN(arrayIndex)) {
        return currentNode;
      }

      nextArray[arrayIndex] = isLastSegment
        ? nextValue
        : updateNode(nextArray[arrayIndex], segmentIndex + 1);
      return nextArray;
    }

    const nextObject = isRecord(currentNode) ? { ...currentNode } : {};

    nextObject[segment] = isLastSegment
      ? nextValue
      : updateNode(nextObject[segment], segmentIndex + 1);

    return nextObject;
  };

  return updateNode(value, 0) as Record<string, PolicyValue>;
};

const removeNestedPolicyArrayItem = (
  value: Record<string, PolicyValue>,
  path: string
): Record<string, PolicyValue> => {
  const segments = path
    .split('.')
    .filter((segment) => segment.trim().length > 0);

  if (segments.length === 0) {
    return value;
  }

  const updateNode = (
    currentValue: PolicyValue | Record<string, PolicyValue>,
    segmentIndex: number
  ): PolicyValue => {
    const segment = segments[segmentIndex];
    const isLastSegment = segmentIndex === segments.length - 1;
    const currentNode = currentValue ?? {};

    if (Array.isArray(currentNode)) {
      const arrayIndex = Number(segment);

      if (Number.isNaN(arrayIndex)) {
        return currentNode;
      }

      if (isLastSegment) {
        return currentNode.filter((_, index) => index !== arrayIndex);
      }

      const nextArray = [...currentNode];
      nextArray[arrayIndex] = updateNode(nextArray[arrayIndex], segmentIndex + 1);
      return nextArray;
    }

    const nextObject = isRecord(currentNode) ? { ...currentNode } : {};

    if (isLastSegment) {
      delete nextObject[segment];
      return nextObject;
    }

    nextObject[segment] = updateNode(nextObject[segment], segmentIndex + 1);
    return nextObject;
  };

  return updateNode(value, 0) as Record<string, PolicyValue>;
};

const resolveTemplateExpression = (
  expression: string,
  expectedValueStructure: Record<string, PolicyValue>
): string => {
  const trimmedExpression = expression.trim();
  const joinMatch = trimmedExpression.match(
    /^(.+)\.join\(\s*(['"])(.*)\2\s*\)$/
  );

  if (joinMatch) {
    const [, valuePath, , separator] = joinMatch;
    const resolvedValue = getNestedPolicyValue(
      expectedValueStructure,
      valuePath.trim()
    );

    if (!Array.isArray(resolvedValue)) {
      return '';
    }

    return resolvedValue.map((entry) => formatPrimitiveValue(entry)).join(separator);
  }

  const resolvedValue = getNestedPolicyValue(
    expectedValueStructure,
    trimmedExpression
  );

  if (resolvedValue === undefined) {
    return '';
  }

  return formatPrimitiveValue(resolvedValue);
};

const renderNormalizedRuleTemplate = (
  template: string,
  expectedValueStructure: Record<string, PolicyValue>
): string =>
  template.replace(
    /\$\{expected_value_structure\.([^}]+)\}/g,
    (_match, expression: string) =>
      resolveTemplateExpression(expression, expectedValueStructure)
  );

const toPolicyCandidateItem = (value: unknown): PolicyCandidateItem | null => {
  if (!isRecord(value) || !isRecord(value.normalized_rule)) {
    return null;
  }

  const normalizedRule = value.normalized_rule;
  const appliesToNormalized = isRecord(normalizedRule.applies_to_normalized)
    ? normalizedRule.applies_to_normalized
    : {};
  const expectedValueStructure = isRecord(
    normalizedRule.expected_value_structure
  )
    ? normalizedRule.expected_value_structure
    : {};
  const normalizationCaution = isRecord(normalizedRule.normalization_caution)
    ? normalizedRule.normalization_caution
    : {};

  const nextItem: PolicyCandidateItem = {
    candidate_id:
      typeof value.candidate_id === 'string' ? value.candidate_id : '',
    normalized_rule: {
      normalized_dimension:
        typeof normalizedRule.normalized_dimension === 'string'
          ? normalizedRule.normalized_dimension
          : '',
      primary_comparison_mechanic:
        typeof normalizedRule.primary_comparison_mechanic === 'string'
          ? normalizedRule.primary_comparison_mechanic
          : '',
      comparison_type:
        typeof normalizedRule.comparison_type === 'string'
          ? normalizedRule.comparison_type
          : '',
      expected_value_structure_type:
        typeof normalizedRule.expected_value_structure_type === 'string'
          ? normalizedRule.expected_value_structure_type
          : '',
      expected_value_structure: Object.fromEntries(
        Object.entries(expectedValueStructure).map(([key, entry]) => [
          key,
          toPolicyValue(entry)
        ])
      ),
      normalized_rule_statement:
        typeof normalizedRule.normalized_rule_statement === 'string'
          ? normalizedRule.normalized_rule_statement
          : '',
      normalized_rule_statement_template:
        typeof normalizedRule.normalized_rule_statement_template === 'string'
          ? normalizedRule.normalized_rule_statement_template
          : '',
      source_text:
        typeof normalizedRule.source_text === 'string'
          ? normalizedRule.source_text
          : '',
      applies_to_normalized: {
        text: toStringArray(appliesToNormalized.text),
        node_refs: toStringArray(appliesToNormalized.node_refs)
      },
      normalization_caution: {
        level:
          typeof normalizationCaution.level === 'string'
            ? normalizationCaution.level
            : '',
        has_soft_or_hard_to_compare_wording: toBooleanValue(
          normalizationCaution.has_soft_or_hard_to_compare_wording
        ),
        notes: toStringArray(normalizationCaution.notes)
      }
    },
    embedding: Array.isArray(value.embedding) ? value.embedding : [],
    embeddingSourceText: Array.isArray(value.embeddingSourceText)
      ? value.embeddingSourceText
      : []
  };

  const hasContent =
    nextItem.candidate_id.trim().length > 0 ||
    nextItem.normalized_rule.normalized_dimension.trim().length > 0 ||
    Object.keys(nextItem.normalized_rule.expected_value_structure).length > 0 ||
    nextItem.normalized_rule.normalized_rule_statement.trim().length > 0 ||
    nextItem.normalized_rule.source_text.trim().length > 0 ||
    nextItem.normalized_rule.applies_to_normalized.text.length > 0 ||
    nextItem.normalized_rule.normalization_caution.notes.length > 0 ||
    nextItem.embedding?.length > 0 ||
    nextItem.embeddingSourceText.length > 0;

  return hasContent ? nextItem : null;
};

const normalizePolicyCategories = (value: unknown): PolicyCandidateItem[] => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      const normalizedItem = toPolicyCandidateItem(item);
      return normalizedItem
        ? [normalizedItem]
        : normalizePolicyCategories(item);
    });
  }

  if (isRecord(value)) {
    const directMatch = toPolicyCandidateItem(value);
    if (directMatch) {
      return [directMatch];
    }

    return Object.values(value).flatMap((item) =>
      normalizePolicyCategories(item)
    );
  }

  return [];
};

const extractPolicyCategories = (data: unknown): PolicyCandidateItem[] =>
  normalizePolicyCategories(data);

const extractPolicyDetails = (
  data: unknown,
  fallbackDetails: PolicyDetailsState
): PolicyDetailsState => {
  const policyDetail =
    isRecord(data) && isRecord(data.policyDetail) ? data.policyDetail : null;
  const policyDetails =
    policyDetail && isRecord(policyDetail.policyDetails)
      ? policyDetail.policyDetails
      : isRecord(data) && isRecord(data.policyDetails)
        ? data.policyDetails
        : null;

  if (!policyDetails) {
    return fallbackDetails;
  }

  return {
    policyName:
      typeof policyDetails.policyName === 'string'
        ? policyDetails.policyName
        : fallbackDetails.policyName,
    version:
      typeof policyDetails.version === 'number'
        ? String(policyDetails.version)
        : typeof policyDetails.version === 'string'
          ? policyDetails.version
          : fallbackDetails.version,
    type:
      typeof policyDetails.type === 'string'
        ? policyDetails.type
        : fallbackDetails.type
  };
};

const getErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Unable to load policy document.';
};

const getEditorStateFromSavedPolicy = (
  value: Record<string, unknown>,
  fallbackState: PolicyEditorState
): PolicyEditorState => {
  return {
    policyDetails: {
      policyName:
        typeof value.policyName === 'string' && value.policyName.trim()
          ? value.policyName
          : fallbackState.policyDetails.policyName,
      version:
        typeof value.version === 'number'
          ? String(value.version)
          : typeof value.version === 'string'
            ? value.version
            : fallbackState.policyDetails.version,
      type:
        typeof value.type === 'string'
          ? value.type
          : fallbackState.policyDetails.type
    },
    policyCategories: fallbackState.policyCategories
  };
};

const PolicyDocumentViewer = () => {
  const { auth, token, user } = useContext(UserContext);
  const { allDocuments, setCurrentDocId } = useContext(DocumentContext);
  const {
    policies,
    getPoliciesLoading,
    getPolicies,
    addPolicy,
    addPolicyLoading,
    updatePolicy,
    updatePolicyLoading
  } = usePolicyContext();
  const { setAlert } = useAlert();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [policyCategories, setPolicyCategories] = useState<
    PolicyCandidateItem[]
  >([]);
  const [policyDetails, setPolicyDetails] =
    useState<PolicyDetailsState>(EMPTY_POLICY_DETAILS);
  const [sourceState, setSourceState] = useState<PolicyEditorState | null>(
    null
  );
  const [initialEditorState, setInitialEditorState] =
    useState<PolicyEditorState | null>(null);
  const [editorInitialized, setEditorInitialized] = useState<boolean>(false);
  const [policyLookupComplete, setPolicyLookupComplete] =
    useState<boolean>(false);
  const [createPolicyLoading, setCreatePolicyLoading] =
    useState<boolean>(false);

  const documentId = searchParams.get('documentId');

  const currentDocument = useMemo(
    () => allDocuments.find((document) => document._id === documentId) ?? null,
    [allDocuments, documentId]
  );

  const existingPolicy = useMemo(
    () => policies.find((policy) => policy.documentId === documentId) ?? null,
    [documentId, policies]
  );

  const saveLoading = addPolicyLoading || updatePolicyLoading;

  const currentEditorState = useMemo(
    () => ({
      policyDetails,
      policyCategories
    }),
    [policyCategories, policyDetails]
  );

  const isDirty =
    Boolean(initialEditorState) &&
    !deepEqual(currentEditorState, initialEditorState);

  const removedCandidateCount = useMemo(() => {
    if (!initialEditorState) {
      return 0;
    }

    return Math.max(
      initialEditorState.policyCategories.length - policyCategories.length,
      0
    );
  }, [initialEditorState, policyCategories.length]);

  const groupedPolicyCategories = useMemo<PolicyCandidateGroup[]>(() => {
    const groups = new Map<string, PolicyCandidateGroup>();

    policyCategories.forEach((item, index) => {
      const dimensionKey = item.normalized_rule.normalized_dimension.trim();
      const groupKey = dimensionKey || '__uncategorized__';

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          dimensionKey: groupKey,
          dimensionLabel: getNormalizedDimensionLabel(dimensionKey),
          items: []
        });
      }

      groups.get(groupKey)?.items.push({
        index,
        item
      });
    });

    return Array.from(groups.values());
  }, [policyCategories]);

  useEffect(() => {
    if (!documentId) {
      return;
    }

    setCurrentDocId(documentId);
  }, [documentId, setCurrentDocId]);

  useEffect(() => {
    setEditorInitialized(false);
    setPolicyLookupComplete(false);
    setInitialEditorState(null);
    setSourceState(null);
    setPolicyDetails(EMPTY_POLICY_DETAILS);
    setPolicyCategories([]);
    setError('');
  }, [documentId]);

  useEffect(() => {
    let cancelled = false;

    const loadPoliciesForDocument = async () => {
      if (!token || !documentId) {
        if (!cancelled) {
          setPolicyLookupComplete(true);
        }
        return;
      }

      await getPolicies();

      if (!cancelled) {
        setPolicyLookupComplete(true);
      }
    };

    loadPoliciesForDocument();

    return () => {
      cancelled = true;
    };
    // `getPolicies` is recreated by context updates; only refetch on document/token change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, token]);

  useEffect(() => {
    let cancelled = false;

    const loadPolicyJson = async () => {
      if (!token || !documentId || !policyLookupComplete) {
        return;
      }

      const policyJsonDocumentId = existingPolicy?._id || documentId;

      setLoading(true);
      setError('');

      let lastError = 'Unable to fetch the policy JSON file.';

      try {
        const presignedUrl = await axios.post(
          `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=getPolicyJson`,
          { documentId: policyJsonDocumentId },
          auth
        );
        const downloadUrl = presignedUrl?.data?.url;

        if (!downloadUrl || downloadUrl === 'locked') {
          throw new Error('No policy JSON presigned URL was returned.');
        }

        const response = await axios.get(downloadUrl, {
          responseType: 'json'
        });

        if (cancelled) {
          return;
        }

        const fallbackDetails = {
          policyName: getPolicyNameFromFileName(
            currentDocument?.documentFileName
          ),
          version: currentDocument?.documentMetaData?.version || '',
          type: currentDocument?.documentType || 'policy_document'
        };
        const initialState = {
          policyCategories: extractPolicyCategories(response.data),
          policyDetails: extractPolicyDetails(response.data, fallbackDetails)
        };

        setSourceState(initialState);
        setInitialEditorState(cloneValue(initialState));
        setLoading(false);
        return;
      } catch (fetchError) {
        lastError = getErrorMessage(fetchError);
      }

      if (!cancelled) {
        setSourceState(null);
        setPolicyCategories([]);
        setPolicyDetails(EMPTY_POLICY_DETAILS);
        setError(lastError);
        setLoading(false);
      }
    };

    loadPolicyJson();

    return () => {
      cancelled = true;
    };
  }, [
    auth,
    currentDocument?.documentFileName,
    currentDocument?.documentType,
    currentDocument?.documentMetaData?.version,
    documentId,
    existingPolicy?._id,
    policyLookupComplete,
    token
  ]);

  useEffect(() => {
    if (
      !sourceState ||
      getPoliciesLoading ||
      !policyLookupComplete ||
      editorInitialized
    ) {
      return;
    }

    const nextState =
      existingPolicy && typeof existingPolicy === 'object'
        ? getEditorStateFromSavedPolicy(
            existingPolicy as Record<string, unknown>,
            sourceState
          )
        : sourceState;

    const clonedState = cloneValue(nextState);

    setPolicyDetails(clonedState.policyDetails);
    setPolicyCategories(clonedState.policyCategories);
    setEditorInitialized(true);
  }, [
    editorInitialized,
    existingPolicy,
    getPoliciesLoading,
    policyLookupComplete,
    sourceState
  ]);

  useEffect(() => {
    if (!sourceState || !editorInitialized || existingPolicy || isDirty) {
      return;
    }

    setPolicyDetails((previousValue) => {
      const nextValue = {
        policyName:
          previousValue.policyName || sourceState.policyDetails.policyName,
        version: previousValue.version || sourceState.policyDetails.version,
        type: previousValue.type || sourceState.policyDetails.type
      };

      return deepEqual(nextValue, previousValue) ? previousValue : nextValue;
    });

    setInitialEditorState((previousValue) => {
      if (!previousValue) {
        return previousValue;
      }

      const nextValue = {
        ...previousValue,
        policyDetails: {
          policyName:
            previousValue.policyDetails.policyName ||
            sourceState.policyDetails.policyName,
          version:
            previousValue.policyDetails.version ||
            sourceState.policyDetails.version,
          type:
            previousValue.policyDetails.type || sourceState.policyDetails.type
        }
      };

      return deepEqual(nextValue, previousValue) ? previousValue : nextValue;
    });
  }, [editorInitialized, existingPolicy, isDirty, sourceState]);

  const handlePolicyDetailsChange = (
    field: keyof PolicyDetailsState,
    value: string
  ) => {
    setPolicyDetails((previousValue) => ({
      ...previousValue,
      [field]: value
    }));
  };

  const updatePolicyCandidate = (
    index: number,
    updater: (currentValue: PolicyCandidateItem) => PolicyCandidateItem
  ) => {
    setPolicyCategories((previousValue) =>
      previousValue.map((item, itemIndex) =>
        itemIndex === index ? updater(item) : item
      )
    );
  };

  const handleRemoveCandidate = (index: number) => {
    setPolicyCategories((previousValue) =>
      previousValue.filter((_, itemIndex) => itemIndex !== index)
    );
  };

  const handleUndoCandidate = (index: number) => {
    const snapshotCandidate = initialEditorState?.policyCategories[index];

    if (!snapshotCandidate) {
      return;
    }

    updatePolicyCandidate(index, () => cloneValue(snapshotCandidate));
  };

  const renderExpectedValueNode = (
    candidateIndex: number,
    path: string,
    value: PolicyValue,
    templateVariablePaths: Set<string>
  ): ReactNode => {
    if (!pathReferencesTemplate(path, templateVariablePaths)) {
      return null;
    }

    if (Array.isArray(value)) {
      const primitiveEntries = value.filter(
        (entry) => !Array.isArray(entry) && !isRecord(entry)
      );
      const isPrimitiveList = primitiveEntries.length === value.length;

      if (isPrimitiveList) {
        const visibleEntries = value
          .map((entry, entryIndex) => ({
            entry,
            entryIndex,
            path: `${path}.${entryIndex}`
          }))
          .filter(({ path: entryPath }) =>
            pathReferencesTemplate(entryPath, templateVariablePaths)
          );

        if (visibleEntries.length === 0) {
          return null;
        }

        return (
          <FormField key={path} label={formatVariableLabel(path)}>
            <SpaceBetween size="s">
              {visibleEntries.map(({ entry, entryIndex, path: entryPath }) => (
                <div
                  key={`${path}-${entryIndex}`}
                  className="flex items-center gap-2"
                >
                  <Input
                    value={formatPrimitiveValue(entry)}
                    onChange={({ detail }) =>
                      updatePolicyCandidate(candidateIndex, (currentValue) => ({
                        ...currentValue,
                        normalized_rule: {
                          ...currentValue.normalized_rule,
                          expected_value_structure: setNestedPolicyValue(
                            currentValue.normalized_rule.expected_value_structure,
                            entryPath,
                            coercePrimitiveValue(detail.value, entry)
                          )
                        }
                      }))
                    }
                  />
                  <button
                    type="button"
                    aria-label={`Remove ${formatVariableLabel(path)} item ${
                      entryIndex + 1
                    }`}
                    className="inline-flex h-8 w-8 items-center justify-center rounded border border-gray-300 text-gray-700 transition hover:bg-gray-100"
                    onClick={() =>
                      updatePolicyCandidate(candidateIndex, (currentValue) => ({
                        ...currentValue,
                        normalized_rule: {
                          ...currentValue.normalized_rule,
                          expected_value_structure: removeNestedPolicyArrayItem(
                            currentValue.normalized_rule.expected_value_structure,
                            entryPath
                          )
                        }
                      }))
                    }
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </SpaceBetween>
          </FormField>
        );
      }

      const childNodes = value
        .map((entry, entryIndex) =>
          renderExpectedValueNode(
            candidateIndex,
            `${path}.${entryIndex}`,
            entry,
            templateVariablePaths
          )
        )
        .filter(Boolean);

      if (childNodes.length === 0) {
        return null;
      }

      return (
        <FormField key={path} label={formatVariableLabel(path)}>
          <SpaceBetween size="s">
            {childNodes.map((childNode, childIndex) => (
              <Box
                key={`${path}-${childIndex}`}
                padding="m"
                className="rounded border border-gray-200 bg-gray-50"
              >
                {childNode}
              </Box>
            ))}
          </SpaceBetween>
        </FormField>
      );
    }

    if (isRecord(value)) {
      const childNodes = Object.entries(value)
        .map(([childKey, childValue]) =>
          renderExpectedValueNode(
            candidateIndex,
            `${path}.${childKey}`,
            childValue as PolicyValue,
            templateVariablePaths
          )
        )
        .filter(Boolean);

      if (childNodes.length === 0) {
        return null;
      }

      return (
        <FormField key={path} label={formatVariableLabel(path)}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {childNodes}
          </div>
        </FormField>
      );
    }

    return (
      <FormField key={path} label={formatVariableLabel(path)}>
        <Input
          value={formatPrimitiveValue(value)}
          onChange={({ detail }) =>
            updatePolicyCandidate(candidateIndex, (currentValue) => ({
              ...currentValue,
              normalized_rule: {
                ...currentValue.normalized_rule,
                expected_value_structure: setNestedPolicyValue(
                  currentValue.normalized_rule.expected_value_structure,
                  path,
                  coercePrimitiveValue(detail.value, value)
                )
              }
            }))
          }
        />
      </FormField>
    );
  };

  const handleReset = () => {
    if (!initialEditorState) {
      return;
    }

    const clonedState = cloneValue(initialEditorState);
    setPolicyDetails(clonedState.policyDetails);
    setPolicyCategories(clonedState.policyCategories);
  };

  // const handleSave = async () => {
  //   if (!documentId) {
  //     setAlert('Missing document id. Unable to save policy.', 'error', 4);
  //     return;
  //   }
  //   const payload = cloneValue(policyCategories) as unknown as Partial<PolicyItem>;

  //   const result =
  //     existingPolicy?._id !== undefined
  //       ? await updatePolicy(existingPolicy._id, payload)
  //       : await addPolicy(payload);

  //   if (axios.isAxiosError(result) || result instanceof Error) {
  //     return;
  //   }
  //   setAlert(
  //     existingPolicy?._id ? 'Policy updated.' : 'Policy saved.',
  //     'success',
  //     4
  //   );
  // };

  const savePolicy = async () => {
    if (!documentId) {
      setAlert('Missing document id. Unable to save policy.', 'error', 4);
      return;
    }

    const isNewPolicy = !existingPolicy?._id;
    const policyName = policyDetails.policyName.trim();
    const typedVersion = toFiniteNumber(policyDetails.version.trim());
    const existingVersion = toFiniteNumber(existingPolicy?.version);
    const nextVersion = existingPolicy?._id
      ? (existingVersion ?? typedVersion ?? 0) + 1
      : (typedVersion ?? 1);
    const policyRecordPayload = {
      customerId: currentDocument?.customerId || user.customerId,
      documentId,
      policyName,
      version: nextVersion,
      type: policyDetails.type.trim()
    };
    const policyBody = cloneValue(policyCategories).map((item) => ({
      ...item,
      normalized_rule: {
        ...item.normalized_rule,
        normalized_rule_statement: renderNormalizedRuleTemplate(
          item.normalized_rule.normalized_rule_statement_template,
          item.normalized_rule.expected_value_structure
        )
      }
    }));
    console.log('policy body before upload', policyBody);

    setCreatePolicyLoading(true);

    try {
      const policyResult = existingPolicy?._id
        ? await updatePolicy(existingPolicy._id, policyRecordPayload)
        : await addPolicy(policyRecordPayload);

      if (axios.isAxiosError(policyResult) || policyResult instanceof Error) {
        throw policyResult;
      }

      const policyDocumentId =
        typeof policyResult?._id === 'string' ? policyResult._id : '';

      if (!policyDocumentId) {
        throw new Error('Policy save did not return a policy ID.');
      }

      const presignedUrl = await axios.post(
        `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=savePolicyJson`,
        { documentId: policyDocumentId },
        auth
      );
      const uploadUrl = presignedUrl?.data?.url;
      if (!uploadUrl) {
        throw new Error('No presigned URL found for policy save.');
      }
      if (uploadUrl === 'locked') {
        throw new Error('File locked by another user.');
      }

      const jsonPayload = JSON.stringify(policyBody);
      const blob = new Blob([jsonPayload], { type: 'application/json' });
      await axios.put(uploadUrl, blob, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // if (isNewPolicy && policyDocumentId !== documentId) {
      //   const deletePresignedUrl = await axios.post(
      //     `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=deletePolicyJson`,
      //     { documentId },
      //     auth
      //   );
      //   const deleteUrl = deletePresignedUrl?.data?.url;

      //   if (!deleteUrl) {
      //     throw new Error('No presigned URL found for policy JSON delete.');
      //   }
      //   if (deleteUrl === 'locked') {
      //     throw new Error('Policy JSON delete is locked by another user.');
      //   }

      //   await axios.delete(deleteUrl);
      // }

      await getPolicies();
      const savedPolicyDetails = {
        policyName,
        version: String(nextVersion),
        type: policyDetails.type.trim()
      };
      const savedPolicyCategories = cloneValue(policyBody);

      setPolicyDetails(savedPolicyDetails);
      setPolicyCategories(savedPolicyCategories);
      setInitialEditorState({
        policyDetails: savedPolicyDetails,
        policyCategories: cloneValue(savedPolicyCategories)
      });
      setAlert(
        existingPolicy?._id ? 'Policy updated.' : 'Policy created.',
        'success',
        4
      );
    } catch (saveError) {
      setAlert(getErrorMessage(saveError), 'error', 4);
    } finally {
      setCreatePolicyLoading(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6">
      <Container
        header={
          <Header
            variant="h1"
            description={
              currentDocument?.documentFileName || documentId || 'Policy viewer'
            }
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {isDirty ? (
                  <Button onClick={handleReset} disabled={saveLoading}>
                    Reset
                  </Button>
                ) : null}
                {existingPolicy?._id && isDirty ? (
                  <Button
                    variant="primary"
                    onClick={savePolicy}
                    loading={createPolicyLoading}
                  >
                    Update Policy
                  </Button>
                ) : null}
                {!existingPolicy?._id ? (
                  <Button
                    variant="primary"
                    onClick={savePolicy}
                    loading={createPolicyLoading}
                    disabled={!documentId || saveLoading}
                  >
                    Create Policy
                  </Button>
                ) : null}
              </SpaceBetween>
            }
          >
            Policy Document Viewer
          </Header>
        }
      >
        <SpaceBetween size="l">
          {!documentId ? (
            <Alert type="error">
              Missing `documentId` in the search params.
            </Alert>
          ) : null}

          {error ? <Alert type="error">{error}</Alert> : null}

          <Container header={<Header variant="h2">Policy Details</Header>}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <FormField label="Policy Name">
                <Input
                  value={policyDetails.policyName}
                  onChange={({ detail }) =>
                    handlePolicyDetailsChange('policyName', detail.value)
                  }
                />
              </FormField>

              <FormField label="Version">
                <Input
                  value={policyDetails.version}
                  onChange={({ detail }) =>
                    handlePolicyDetailsChange('version', detail.value)
                  }
                />
              </FormField>

              <FormField label="Type">
                <Input
                  value={policyDetails.type}
                  onChange={({ detail }) =>
                    handlePolicyDetailsChange('type', detail.value)
                  }
                />
              </FormField>
            </div>
          </Container>

          <Header counter={`(${policyCategories.length})`}>
            Policy Candidates
          </Header>

          {!loading && removedCandidateCount > 0 ? (
            <Alert type="warning">
              {removedCandidateCount} candidate
              {removedCandidateCount === 1 ? '' : 's'} removed from the current
              state.
            </Alert>
          ) : null}

          {loading ? (
            <Box textAlign="center" padding={{ vertical: 'xl' }}>
              <Spinner size="large" />
            </Box>
          ) : null}

          {!loading && policyCategories.length === 0 ? (
            <Box margin={{ vertical: 'xs' }} textAlign="center">
              {documentId
                ? 'No policy candidates found.'
                : 'Policy candidates will appear here.'}
            </Box>
          ) : null}

          {!loading ? (
            <SpaceBetween size="xl">
              {groupedPolicyCategories.map((group) => (
                <section key={group.dimensionKey}>
                  <SpaceBetween size="m">
                    <Header counter={`(${group.items.length})`} variant="h2">
                      {group.dimensionLabel}
                    </Header>

                    <div className="grid grid-cols-1 gap-4">
                      {group.items.map(({ item, index }) => {
                        const initialCandidate =
                          initialEditorState?.policyCategories[index];
                        const isCandidateDirty =
                          initialCandidate === undefined ||
                          !deepEqual(initialCandidate, item);
                        const templateVariablePaths = getTemplateVariablePaths(
                          item.normalized_rule
                            .normalized_rule_statement_template
                        );
                        const expectedValueNodes = Object.entries(
                          item.normalized_rule.expected_value_structure
                        )
                          .map(([fieldKey, fieldValue]) =>
                            renderExpectedValueNode(
                              index,
                              fieldKey,
                              fieldValue,
                              templateVariablePaths
                            )
                          )
                          .filter(Boolean);

                        return (
                          <Container
                            key={`${item.candidate_id || 'candidate'}-${index}`}
                            header={
                              <Header
                                variant="h3"
                                actions={
                                  <SpaceBetween
                                    direction="horizontal"
                                    size="xs"
                                  >
                                    {isCandidateDirty ? (
                                      <button
                                        type="button"
                                        aria-label={`Save ${
                                          item.candidate_id ||
                                          `candidate ${index + 1}`
                                        }`}
                                        title="Save policy"
                                        disabled={!documentId || saveLoading}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded border border-gray-300 text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                        onClick={savePolicy}
                                      >
                                        <Save size={16} color="green" />
                                      </button>
                                    ) : null}
                                    {isCandidateDirty && initialCandidate ? (
                                      <button
                                        type="button"
                                        aria-label={`Undo changes to ${
                                          item.candidate_id ||
                                          `candidate ${index + 1}`
                                        }`}
                                        title="Undo changes"
                                        className="inline-flex h-8 w-8 items-center justify-center rounded border border-gray-300 text-gray-700 transition hover:bg-gray-100"
                                        onClick={() =>
                                          handleUndoCandidate(index)
                                        }
                                      >
                                        <RotateCcw size={16} />
                                      </button>
                                    ) : null}
                                    <button
                                      type="button"
                                      aria-label={`Remove ${
                                        item.candidate_id ||
                                        `candidate ${index + 1}`
                                      }`}
                                      className="inline-flex h-8 w-8 items-center justify-center rounded border border-gray-300 text-gray-700 transition hover:bg-gray-100"
                                      onClick={() =>
                                        handleRemoveCandidate(index)
                                      }
                                    >
                                      <Trash2 size={16} color="red" />
                                    </button>
                                  </SpaceBetween>
                                }
                              >
                                {item.candidate_id ||
                                  `Policy Candidate ${index + 1}`}
                              </Header>
                            }
                          >
                            <SpaceBetween size="l">
                              <SpaceBetween size="s">
                                <Header variant="h3">Editable Items</Header>

                                {expectedValueNodes.length > 0 ? (
                                  <div className="grid grid-cols-1 gap-4">
                                    {expectedValueNodes}
                                  </div>
                                ) : (
                                  <Box color="text-body-secondary">
                                    No Editable items.
                                  </Box>
                                )}
                              </SpaceBetween>

                              <FormField label="Normalized Rule">
                                <Box
                                  padding="m"
                                  className="rounded border border-gray-300 bg-gray-50 whitespace-pre-wrap"
                                >
                                  {renderNormalizedRuleTemplate(
                                    item.normalized_rule
                                      .normalized_rule_statement_template,
                                    item.normalized_rule
                                      .expected_value_structure
                                  ) || (
                                    <span className="text-gray-500">
                                      No normalized rule statement template
                                      found.
                                    </span>
                                  )}
                                </Box>
                              </FormField>

                              <FormField label="Source Text">
                                <Box
                                  padding="m"
                                  className="rounded border border-gray-300 bg-gray-50 whitespace-pre-wrap"
                                >
                                  {item.normalized_rule.source_text || (
                                    <span className="text-gray-500">
                                      No source text found.
                                    </span>
                                  )}
                                </Box>
                              </FormField>
                            </SpaceBetween>
                          </Container>
                        );
                      })}
                    </div>
                  </SpaceBetween>
                </section>
              ))}
            </SpaceBetween>
          ) : null}
        </SpaceBetween>
      </Container>
    </div>
  );
};

export default PolicyDocumentViewer;
